import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { Loader2, ShieldCheck, BookOpen, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import TechBackground from "@/components/TechBackground";

const REMEMBER_KEY = "tr_remember_email";
const STAY_SIGNED_IN_KEY = "tr_stay_signed_in";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatErr(detail) {
    if (!detail) return "Something went wrong";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
    return String(detail);
}

/**
 * Simple email + password sign-in screen (iter 80).
 *
 * Replaces the previous magic-link / 6-digit OTP flow on the user's
 * request — admin and clinic users now sign in with their email and
 * password. No mode switch, no break-glass toggle, no OTP boxes.
 *
 * We still attempt a silent trusted-device exchange on mount so users
 * who previously opted into "remember this device for 90 days" don't
 * get prompted again until the cookie expires. This is transparent UI
 * (no extra buttons) — pure quality-of-life carry-over from iter 71.
 */
export default function Login() {
    const { login } = useAuth();
    const { t } = useI18n();
    const nav = useNavigate();
    const loc = useLocation();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    // "Remember my email" — quietly pre-fills the email field on this device.
    // Hidden behind the visible 30-day checkbox below; we always remember
    // the email when the user opts in to the 30-day session (it would be
    // confusing to persist for 30 days but force-retype the email).
    const [remember, setRemember] = useState(false);
    // 30-day passkey — when checked, the backend mints a long-lived
    // `tr_trusted_device` cookie so the next visit auto-logs in via the
    // silent exchange in `useEffect` below. Default ON because the user
    // explicitly wants to stop typing their password.
    const [staySignedIn, setStaySignedIn] = useState(true);
    const [busy, setBusy] = useState(false);
    // Hides the form until the silent trusted-device exchange resolves —
    // avoids a 100-ms form flash for users with a valid 90-day cookie.
    const [exchanging, setExchanging] = useState(true);

    useEffect(() => {
        try {
            const saved = localStorage.getItem(REMEMBER_KEY);
            if (saved) { setEmail(saved); setRemember(true); }
            // Honour the user's last-known 30-day preference so unchecking
            // it stays unchecked across visits (rather than silently re-
            // arming itself every time they reach /login).
            const stayPref = localStorage.getItem(STAY_SIGNED_IN_KEY);
            if (stayPref === "0") setStaySignedIn(false);
        } catch { /* localStorage disabled */ }

        let cancelled = false;
        axios.post(`${API}/auth/trusted-device/exchange`)
            .then((res) => {
                if (cancelled) return;
                const user = res.data.user;
                toast.success(`Welcome back, ${user.dentist_name || user.name || user.email}`);
                const dest = user.role && user.role !== "client" ? "/admin" : "/portal";
                window.location.replace(loc.state?.from || dest);
            })
            .catch(() => { if (!cancelled) setExchanging(false); });
        return () => { cancelled = true; };
    }, []);

    const onSubmit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const user = await login(email, password, staySignedIn);
            try {
                // Always remember the email when the user opts into the
                // 30-day session — they shouldn't have to retype their email
                // every visit while their cookie is still warm. Otherwise
                // honour the email-only remember checkbox (legacy behaviour).
                if (remember || staySignedIn) localStorage.setItem(REMEMBER_KEY, email);
                else localStorage.removeItem(REMEMBER_KEY);
                localStorage.setItem(STAY_SIGNED_IN_KEY, staySignedIn ? "1" : "0");
            } catch { /* noop */ }
            toast.success(`Welcome, ${user.dentist_name || user.name || user.email}`);
            const dest = user.role && user.role !== "client" ? "/admin" : "/portal";
            nav(loc.state?.from || dest, { replace: true });
        } catch (err) {
            toast.error(formatErr(err?.response?.data?.detail) || err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="login-page" className="min-h-[calc(100vh-68px)] grid lg:grid-cols-2 bg-white">
            {/* Visual side */}
            <div className="relative hidden lg:block bg-[#0B1120] tr-grain overflow-hidden">
                <TechBackground variant="dark" density={1.1} />
                <div className="absolute -top-32 -right-20 w-[500px] h-[500px] tr-glow bg-cyan-500/30" />
                <div className="absolute bottom-0 -left-32 w-[420px] h-[420px] tr-glow bg-teal-500/25" />
                <div className="relative h-full flex flex-col justify-between p-12 text-white">
                    <div />
                    <div>
                        <div className="tr-overline text-cyan-400 mb-3">· Secure · Clinic portal</div>
                        <h1 className="font-display font-light text-5xl tracking-tight leading-[1.05]">
                            One signal across<br /><span className="italic text-cyan-300/90 font-extralight">every printed case.</span>
                        </h1>
                        <p className="mt-6 text-white/70 max-w-md text-[15px] leading-relaxed">
                            Sign in with your email and password. Forgot your password? Use the link below the sign-in form to reset it yourself.
                        </p>
                    </div>
                    <div className="flex items-center gap-2 text-white/50 font-mono-tr text-[11px] tracking-[0.2em] uppercase">
                        <ShieldCheck size={14} /> Encrypted · TLS · PHI-safe
                    </div>
                </div>
            </div>

            {/* Form side */}
            <div className="flex items-center justify-center p-6 lg:p-12">
                {exchanging ? (
                    <div data-testid="login-exchanging" className="w-full max-w-md text-center">
                        <Loader2 size={28} className="mx-auto text-cyan-600 animate-spin" />
                        <p className="mt-4 text-[13px] text-slate-500 font-mono-tr tracking-wide">
                            Checking trusted device…
                        </p>
                    </div>
                ) : (
                    <div className="w-full max-w-md">
                        <div className="tr-overline mb-3">· Toothrocket · Sign in</div>
                        <h1 className="font-display font-light text-4xl tracking-tight leading-tight text-slate-900">
                            Welcome back.
                        </h1>
                        <p className="mt-2 text-slate-600 text-[14.5px]">
                            Enter your email and password to access the portal.
                        </p>

                        <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="login-form">
                            <label className="block">
                                <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-slate-500 mb-2">Email</div>
                                <input
                                    data-testid="login-email"
                                    type="email"
                                    required
                                    autoFocus={!email}
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 transition outline-none text-[14.5px]"
                                    placeholder="dentist@clinic.com"
                                />
                            </label>
                            <label className="block">
                                <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-slate-500 mb-2">Password</div>
                                <div className="relative">
                                    <input
                                        data-testid="login-password"
                                        type={showPassword ? "text" : "password"}
                                        required
                                        autoFocus={!!email}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full px-4 py-3 pr-12 rounded-lg border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 transition outline-none text-[14.5px]"
                                        placeholder="••••••••"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword((v) => !v)}
                                        data-testid="login-password-toggle"
                                        aria-label={showPassword ? "Hide password" : "Show password"}
                                        className="absolute right-2.5 top-1/2 -translate-y-1/2 p-2 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                                    >
                                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </button>
                                </div>
                            </label>
                            <div className="space-y-2.5">
                                <label className="flex items-start gap-2.5 select-none cursor-pointer text-[13px] text-slate-700">
                                    <input
                                        data-testid="login-stay-signed-in"
                                        type="checkbox"
                                        checked={staySignedIn}
                                        onChange={(e) => setStaySignedIn(e.target.checked)}
                                        className="mt-0.5 w-4 h-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500 focus:ring-2 cursor-pointer"
                                    />
                                    <span>
                                        <span className="font-medium text-slate-800">Keep me signed in for 30 days</span>
                                        <span className="block text-[11.5px] text-slate-500 mt-0.5">
                                            Skip the password on this device. Uncheck on shared computers.
                                        </span>
                                    </span>
                                </label>
                                <label className="flex items-center gap-2.5 select-none cursor-pointer text-[12px] text-slate-500 ms-7">
                                    <input
                                        data-testid="login-remember"
                                        type="checkbox"
                                        checked={remember}
                                        onChange={(e) => setRemember(e.target.checked)}
                                        className="w-3.5 h-3.5 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500 focus:ring-2 cursor-pointer"
                                    />
                                    Also remember my email on this device
                                </label>
                            </div>
                            <button
                                data-testid="login-submit"
                                type="submit"
                                disabled={busy}
                                className="w-full tr-btn justify-center !py-3.5 disabled:opacity-60"
                            >
                                {busy ? (<><Loader2 size={16} className="animate-spin" /> Signing in…</>) : "Sign in"}
                            </button>
                            <div className="mt-3 text-center">
                                <Link
                                    to="/forgot-password"
                                    data-testid="login-forgot-password"
                                    className="text-[12.5px] text-slate-500 hover:text-cyan-700 underline-offset-2 hover:underline"
                                >
                                    Forgot your password?
                                </Link>
                            </div>
                        </form>

                        <div className="mt-10 pt-6 border-t border-slate-100 text-[13px] text-slate-500">
                            {t("login.footer")}
                        </div>
                        <a
                            href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/clinic-guide`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="login-clinic-guide"
                            className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-cyan-50 border border-cyan-200 text-cyan-800 hover:bg-cyan-100 transition-colors text-[12.5px] font-medium"
                        >
                            <BookOpen size={14} /> Read the Clinic Guide
                        </a>
                        <Link
                            to="/contact?subject=login-support"
                            data-testid="login-contact-support"
                            className="mt-3 ml-2 inline-flex items-center gap-1.5 text-[12.5px] text-slate-500 hover:text-cyan-700 underline-offset-2 hover:underline"
                        >
                            Trouble signing in? Contact support →
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
}
